import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration } from '../lib/utils';
import {
    createMemberShip,
    MembershipCreationResult,
    MemberShipApiType,
    deleteMemberShip,
    getAllMembershipForMember,
    getMembershipTemplatesListAPI,
    updateMemberShip,
    UpdateMemberShipApiType,
    patchMonthCommission,
    patchMemberTicketMonthCommission,
    PatchMonthCommissionApiType,
    deleteMonthCommission,
    deleteMemberTicketMonthCommission,
} from '../api/memberShip';
import { Issuer } from '../api/center';
import { MemberMembershipDataType } from './useMembershipQueryService';
import { MembershipTemplateDataType } from './useMembershipTemplateQueryService';
import { invalidateSuperAdminDashboardQueries } from '../lib/superAdminCache';

type Props = {
    getAllMembershipForMemberParams?: {
        memberId: string;
        coachId: string;
    };
    fetchAllMembershipTemplatesList?: boolean;
    onCreateMemberShipSuccess?: (result: MembershipCreationResult) => void;
    /** 발급이 201 이 아닌 채로 끝났을 때. 공유 상품 발급 실패(409) 안내가 여기로 온다 */
    onCreateMemberShipFail?: (result: MembershipCreationResult | null | undefined) => void;
    onUpdatedMemberShipSuccess?: () => void;
    /** 수정 요청이 200 이 아닌 채로 끝났을 때. data 는 서버의 구조화 오류 본문이다. */
    onUpdatedMemberShipFail?: (status?: number, data?: unknown) => void;
    onDeleteMemberShipSuccess?: () => void;
    onDeleteMemberShipFail?: (status?: number, data?: unknown) => void;
    onPatchMonthCommissionSuccess?: () => void;
    /**
     * 커스텀 요율 저장 실패. status 가 undefined 면 네트워크 단계에서 끊긴 경우.
     *
     * 여기가 비어 있던 것이 실제 CS 원인이었다 — 실패해도 모달이 그냥 닫혀서 관리자는
     * 저장된 줄 알고 있고, 정산 금액만 안 바뀌어 있었다(U5).
     */
    onPatchMonthCommissionFail?: (status?: number) => void;
    /** 수강권별 커스텀 요율 해제 성공 */
    onDeleteMonthCommissionSuccess?: () => void;
    /** 해제 실패. status 가 undefined 면 네트워크 단계에서 끊긴 경우 */
    onDeleteMonthCommissionFail?: (status?: number) => void;
};

const useMembershipService = ({
    getAllMembershipForMemberParams,
    fetchAllMembershipTemplatesList,
    onCreateMemberShipSuccess,
    onCreateMemberShipFail,
    onUpdatedMemberShipSuccess,
    onUpdatedMemberShipFail,
    onDeleteMemberShipSuccess,
    onDeleteMemberShipFail,
    onPatchMonthCommissionSuccess,
    onPatchMonthCommissionFail,
    onDeleteMonthCommissionSuccess,
    onDeleteMonthCommissionFail,
}: Props) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();

    const invalidateMembershipRelatedQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getUnpaidPaymentsQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getSalesListQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] }),
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleFeeQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleSessionQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
            queryClient.invalidateQueries({ queryKey: ['groupTicket'] }),
            queryClient.invalidateQueries({ queryKey: ['publicMember'] }),
            queryClient.invalidateQueries({ queryKey: ['membershipChangeLog'] }),
            invalidateSuperAdminDashboardQueries(queryClient),
        ]);
    };

    // 모든 회원권 가져오기
    const { data: allMembershipListData, isLoading: allMembershipListDataLoading } = useQuery(
        ['getAllMembershipForMemberQuery', getAllMembershipForMemberParams?.memberId ?? '', authorization],
        async () =>
            await getAllMembershipForMember(
                getAllMembershipForMemberParams?.coachId ?? '',
                getAllMembershipForMemberParams?.memberId ?? '',
                authorization
            ),
        {
            enabled:
                !!authorization &&
                !!getAllMembershipForMemberParams?.memberId &&
                !!getAllMembershipForMemberParams?.coachId,
            suspense: true,
        }
    );
    // 나의 수강권들 상품 조회
    const { data: allMembershipTemplatesListData, isLoading: allMembershipTemplatesListLoading } = useQuery(
        ['getMembershipTemplatesListAPIQuery', authorization],
        async () => await getMembershipTemplatesListAPI(authorization, authObject?.centerId),
        {
            enabled: !!authorization && fetchAllMembershipTemplatesList,
            suspense: true,
        }
    );

    // 회원권 생성
    const createMemberShipMutation = useMutation(
        ['createMemberShipMutation'],
        async (params: MemberShipApiType & { accessToken: string }) => createMemberShip(params),
        {
            onError: (error) => {
                console.log(error);
                onCreateMemberShipFail && onCreateMemberShipFail(null);
            },
            onSuccess: async (result: MembershipCreationResult) => {
                if (result?.status === 201) {
                    onCreateMemberShipSuccess && onCreateMemberShipSuccess(result);
                    void invalidateMembershipRelatedQueries();
                    return;
                }
                // 201 이 아니면 수강권이 만들어지지 않았다. 여기서 알리지 않으면
                // 발급 여부를 알 수 없는 무음 실패가 된다.
                onCreateMemberShipFail && onCreateMemberShipFail(result);
            },
        }
    );
    const updateMemberShipMutation = useMutation(
        ['updateMemberShipMutation'],
        async (params: UpdateMemberShipApiType) => updateMemberShip(params),
        {
            onError: (error) => {
                console.log(error);
                onUpdatedMemberShipFail && onUpdatedMemberShipFail();
            },
            // result 를 구조분해하지 않는다 — 실패 응답에서 undefined 가 들어오면 콜백 자체가 터져
            //   성공도 실패도 화면에 안 남는 무음 실패가 된다.
            onSuccess: async (result) => {
                if (result?.status === 200) {
                    onUpdatedMemberShipSuccess && onUpdatedMemberShipSuccess();
                    void invalidateMembershipRelatedQueries();
                    return;
                }
                onUpdatedMemberShipFail && onUpdatedMemberShipFail(result?.status, result?.data);
            },
        }
    );
    const deleteMemberShipMutation = useMutation(
        ['deleteMemberShipMutation'],
        async (params: {
            membershipId: string;
            accessToken: string;
            source?: 'pt' | 'group';
            centerId?: string;
        }) => deleteMemberShip(params.membershipId, params.accessToken, params.source, params.centerId),
        {
            onError: (error) => {
                console.log(error);
                onDeleteMemberShipFail && onDeleteMemberShipFail();
            },
            onSuccess: async (result) => {
                // PT: 204, 그룹: 200
                if (result?.status === 204 || result?.status === 200) {
                    onDeleteMemberShipSuccess && onDeleteMemberShipSuccess();
                    void invalidateMembershipRelatedQueries();
                    return;
                }
                onDeleteMemberShipFail && onDeleteMemberShipFail(result?.status, result?.data);
            },
        },
    );
    const patchMonthCommissionMutation = useMutation(
        ['patchMonthCommissionMutation'],
        async (
            params: Omit<PatchMonthCommissionApiType, 'accessToken'> & { source?: 'pt' | 'group' },
        ) => {
            // 그룹 수강권은 MemberTicket 전용 엔드포인트로 분기 (커밋 99a96b2c).
            // 이때 params.membershipId는 실제 MemberTicket._id가 들어온다.
            if (params.source === 'group') {
                return patchMemberTicketMonthCommission({
                    centerId: authObject?.centerId,
                    memberTicketId: params.membershipId,
                    year: params.year,
                    month: params.month,
                    commissionType: params.commissionType,
                    commission: params.commission,
                    accessToken: authorization,
                });
            }
            return patchMonthCommission({
                membershipId: params.membershipId,
                year: params.year,
                month: params.month,
                commissionType: params.commissionType,
                commission: params.commission,
                accessToken: authorization,
            });
        },
        {
            onError: (error) => {
                console.log(error);
                onPatchMonthCommissionFail && onPatchMonthCommissionFail();
            },
            // 200 이 아닌 응답도 실패로 알린다. 종료된 수강권의 커스텀 요율은 서버가 열어 줬지만
            // (G3) 센터 권한·계보 상태에 따라 여전히 거절될 수 있고, 조용히 넘어가면
            // 관리자는 강사 급여가 바뀐 줄 안다.
            onSuccess: async (result) => {
                if (result?.status === 200) {
                    onPatchMonthCommissionSuccess && onPatchMonthCommissionSuccess();
                    void invalidateMembershipRelatedQueries();
                    return;
                }
                onPatchMonthCommissionFail && onPatchMonthCommissionFail(result?.status);
            },
        },
    );

    // 수강권별 커스텀 요율 해제 (PLAN_TIER_CUSTOM_COMMISSION §5.6 / D5)
    // patchMonthCommissionMutation과 동일하게 source==='group'으로 엔드포인트를 분기한다.
    // 서버는 전달한 year/month 이하의 모든 커스텀 설정을 제거한다 — 과거 월 정산 표시도 함께 바뀐다.
    const deleteMonthCommissionMutation = useMutation(
        ['deleteMonthCommissionMutation'],
        async (params: { membershipId: string; year: string; month: string; source?: 'pt' | 'group' }) => {
            // 그룹 row의 membershipId에는 MemberTicket._id가 들어온다 (저장 경로와 동일).
            if (params.source === 'group') {
                return deleteMemberTicketMonthCommission({
                    centerId: authObject?.centerId,
                    memberTicketId: params.membershipId,
                    year: params.year,
                    month: params.month,
                    accessToken: authorization,
                });
            }
            return deleteMonthCommission({
                membershipId: params.membershipId,
                year: params.year,
                month: params.month,
                accessToken: authorization,
            });
        },
        {
            onError: (error) => {
                console.log(error);
                onDeleteMonthCommissionFail && onDeleteMonthCommissionFail();
            },
            // result 를 구조분해하지 않는다 — 실패 응답에서 undefined 가 들어오면 무음 실패가 된다.
            onSuccess: async (result) => {
                if (result?.status === 200 || result?.status === 204) {
                    onDeleteMonthCommissionSuccess && onDeleteMonthCommissionSuccess();
                    void invalidateMembershipRelatedQueries();
                    return;
                }
                onDeleteMonthCommissionFail && onDeleteMonthCommissionFail(result?.status);
            },
        },
    );

    return {
        allMembershipListData: allMembershipListData?.data?.membersMemberships || ([] as MemberMembershipDataType[]),
        allMembershipListDataLoading,
        allMembershipTemplatesListData:
            allMembershipTemplatesListData?.data?.membershipTemplates || ([] as MembershipTemplateDataType[]),
        allMembershipTemplatesListLoading,
        createMemberShipMutation,
        updateMemberShipMutation,
        deleteMemberShipMutation,
        patchMonthCommissionMutation,
        deleteMonthCommissionMutation,
    };
};

export default useMembershipService;
